import { compareDisplayNames } from '@utils/string.util';
import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import { MinutesLabelPipe } from '@pipes/minutes-label.pipe';
import {
  CreateQTSFormGroup,
  DeviceModel,
  DeviceTypeValidatorViewModel,
} from '@services/qts-formgroup';
import {
  DeviceSettingBase,
  resetSensorSettings,
  sensorsHavingForm,
} from '@utils/device-setting.util';
import { formatISO } from 'date-fns';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';

/** ダイアログのViewModel */
export type DialogOption = {
  updating: boolean;
  tenantId: string;
  userId: string;
  deviceTypes: DeviceTypeViewModel[];
  target?: AssetTypeViewModel;
  isDisabled: boolean;
};

/** 設定テンプレート */
export type AssetTypeViewModel = DeviceSettingBase & {
  /** アセットタイプID */
  assettypeId: string;
  /** アセットタイプ名 */
  assettypeName: string;
  /** テナントID */
  tenantId: string;
  /** デバイスステータス */
  deviceActiveStatus: boolean;
  /** デバイス種類ID */
  deviceTypeId: string;
  deviceTypeKey: string;
  /** センサーデータ収集間隔 */
  samplingFrequency: number | null;
  /** クラウドへのデータ送信間隔 */
  reportingFrequency: number | null;
  /** 作成者 */
  createdBy: string;
  /** 更新者 */
  updatedBy: string;
  /** 作成日時 */
  createdAt: string;
  /** 更新日時 */
  updatedAt: string;
};

/** 設定テンプレートのResponseModel */
export type AssetTypeResponseModel = {
  /** 設定テンプレートID */
  assetTypeId: string;
  /** 設定テンプレート名 */
  assetTypeName: string;
  /** デバイスのアクティブ状態*/
  deviceActiveStatus: boolean;
  /** デバイス種類ID */
  deviceTypeId: string;
  deviceTypeKey: string;
  /** 測定間隔 */
  samplingFrequency: number | null;
  /** 送信間隔 */
  reportingFrequency: number | null;
  /** 位置検知 */
  locationDetected?: boolean;
  /** 位置測位技術優先度1 */
  locationPriority_01?: string;
  /** 位置測位技術優先度2 */
  locationPriority_02?: string;
  /** 位置測位技術優先度3 */
  locationPriority_03?: string;
  /** 落下検知イベント */
  fallDetected?: boolean;
  /** 動作検知イベント */
  mcdDetected?: boolean;
  /** 明暗検知イベント */
  lightExposureDetected?: boolean;
  /** 明暗検知イベント設定値 */
  lightExposureDetectValue?: string;
  /** 衝撃検知イベント */
  shockDetected?: boolean;
  /** 衝撃検知イベント設定値 */
  shockDetectedDetectValue?: number;
  /** 温度センサー */
  temperature?: boolean;
  /** 温度アラート上限逸脱 */
  temperatureAlertMax?: number;
  /** 温度アラート下限逸脱 */
  temperatureAlertMin?: number;
  /** 温度アラート上限警告 */
  temperaturePrealertMax?: number;
  /** 温度アラート下限警告 */
  temperaturePrealertMin?: number;
  /** 気圧センサー */
  pressure?: boolean;
  /** 気圧センサー上限逸脱 */
  pressureAlertMax?: number;
  /** 気圧センサー下限逸脱 */
  pressureAlertMin?: number;
  /** 気圧センサー上限警告 */
  pressurePrealertMax?: number;
  /** 気圧センサー下限警告 */
  pressurePrealertMin?: number;
  /** 湿度センサー */
  humidity?: boolean;
  /** 湿度センサー上限逸脱 */
  humidityAlertMax?: number;
  /** 湿度センサー下限逸脱 */
  humidityAlertMin?: number;
  /** 湿度センサー上限警告 */
  humidityPrealertMax?: number;
  /** 湿度センサー下限警告 */
  humidityPrealertMin?: number;
  /** 傾斜センサー */
  tilt?: boolean;
  /** 傾斜センサー傾斜許容角度 */
  tiltAlertMax?: number;
  /** 照度センサー */
  light?: boolean;
  /** 照度センサー上限逸脱 */
  lightAlertMax?: number;
  /** 照度センサー下限逸脱 */
  lightAlertMin?: number;
  /** 照度センサー上限警告 */
  lightPrealertMax?: number;
  /** 照度センサー下限警告 */
  lightPrealertMin?: number;
  /** フライトモード */
  fmd?: boolean;
  /** 配送機能での利用許可 */
  shipmentTracking?: boolean;
  /** 作成者 */
  createdBy: string;
  /** 更新者 */
  updatedBy: string;
  /** 作成日時 */
  createdAt: string;
  /** 更新日時 */
  updatedAt: string;
};

/** デバイス種類のViewModel */
export type DeviceTypeViewModel = {
  /** デバイス種類内部キー */
  deviceTypeKey: string;
  /** デバイス種類ID */
  deviceTypeId: string;
  /** 表示名 */
  displayName: string;
  /** 説明文 */
  description?: string;
  /** 測定間隔 */
  samplingFrequency?: number[];
  /** 送信間隔 */
  reportingFrequency?: number[];
  /** 温度センサー */
  temperatureSensor?: boolean;
  /** 気圧センサー */
  pressureSensor?: boolean;
  /** 湿度センサー */
  humiditySensor?: boolean;
  /** 傾斜センサー */
  tiltSensor?: boolean;
  /** 照度センサー */
  lightSensor?: boolean;
  /** 位置測位 */
  locationSensor?: boolean;
  /** 位置測位_GNSS */
  locationTechGnss?: boolean;
  /** 位置測位_Wi-Fi */
  locationTechWifi?: boolean;
  /** 位置測位_Cellular */
  locationTechCellular?: boolean;
  /** 落下イベント検知 */
  fallDetected?: boolean;
  /** 動作イベント検知 */
  mcdDetected?: boolean;
  /** 明暗イベント検知 */
  lightExposureDetected?: boolean;
  /** 明暗値 */
  lightExposureDetectedValue?: string;
  /** 衝撃イベント検知 */
  shockDetected?: boolean;
  /** 衝撃値 */
  shockDetectedDetectValue?: number;
  /** フライトモード */
  fmd?: boolean;
  /** デフォルトのtrackerの設定値 */
  defaultTracker?: DefaultTrackerModel;
};

/** トラッカーのデフォルト値のModel */
export type DefaultTrackerModel = {
  /** 測定間隔 */
  samplingFrequency?: number;
  /** 送信間隔 */
  reportingFrequency?: number;
  /** 位置検知 */
  locationDetected?: boolean;
  /** 位置測位技術優先度1 */
  locationPriority_01?: string;
  /** 位置測位技術優先度2 */
  locationPriority_02?: string;
  /** 位置測位技術優先度3 */
  locationPriority_03?: string;
  /** 落下検知イベント */
  fallDetected?: boolean;
  /** 動作検知イベント */
  mcdDetected?: boolean;
  /** 明暗検知イベント */
  lightExposureDetected?: boolean;
  /** 明暗検知イベント設定値 */
  lightExposureDetectValue?: string;
  /** 衝撃検知イベント */
  shockDetected?: boolean;
  /** 衝撃検知イベント設定値 */
  shockDetectedDetectValue?: number;
  /** 温度センサー */
  temperature?: boolean;
  /** 温度アラート上限逸脱 */
  temperatureAlertMax?: number;
  /** 温度アラート下限逸脱 */
  temperatureAlertMin?: number;
  /** 温度アラート上限警告 */
  temperaturePrealertMax?: number;
  /** 温度アラート下限警告 */
  temperaturePrealertMin?: number;
  /** 気圧センサー */
  pressure?: boolean;
  /** 気圧センサー上限逸脱 */
  pressureAlertMax?: number;
  /** 気圧センサー下限逸脱 */
  pressureAlertMin?: number;
  /** 気圧センサー上限警告 */
  pressurePrealertMax?: number;
  /** 気圧センサー下限警告 */
  pressurePrealertMin?: number;
  /** 湿度センサー */
  humidity?: boolean;
  /** 湿度センサー上限逸脱 */
  humidityAlertMax?: number;
  /** 湿度センサー下限逸脱 */
  humidityAlertMin?: number;
  /** 湿度センサー上限警告 */
  humidityPrealertMax?: number;
  /** 湿度センサー下限警告 */
  humidityPrealertMin?: number;
  /** 傾斜センサー */
  tilt?: boolean;
  /** 傾斜センサー上限逸脱 */
  tiltAlertMax?: number;
  /** 傾斜センサー下限逸脱 */
  tiltAlertMin?: number;
  /** 傾斜センサー上限警告 */
  tiltPrealertMax?: number;
  /** 傾斜センサー下限警告 */
  tiltPrealertMin?: number;
  /** 照度センサー */
  light?: boolean;
  /** 照度センサー上限逸脱 */
  lightAlertMax?: number;
  /** 照度センサー下限逸脱 */
  lightAlertMin?: number;
  /** 照度センサー上限警告 */
  lightPrealertMax?: number;
  /** 照度センサー下限警告 */
  lightPrealertMin?: number;
  /** フライトモード */
  fmd?: boolean;
  /** 配送機能 */
  shipmentTracking: boolean;
};

/** フォームグループ */
type AssetTypeFormGroup = FormGroup<{
  /** デバイス名 */
  assetTypeName: FormControl<string>;
  /** デバイス種類ID */
  deviceTypeId: FormControl<string>;
  /** 測定間隔 */
  samplingFrequency: FormControl<number | null>;
  /** 送信間隔 */
  reportingFrequency: FormControl<number | null>;
  /** 位置検知 */
  locationDetected: FormControl<boolean>;
  /** 位置測位技術優先度 */
  locationPriority: FormGroup<{
    locationPriority1: FormControl<string | null>;
    locationPriority2: FormControl<string | null>;
    locationPriority3: FormControl<string | null>;
  }>;
  /** 落下検知 */
  fallDetected: FormControl<boolean>;
  /** 動作検知 */
  mcdDetected: FormControl<boolean>;
  /** 明暗検知 */
  lightExposureDetected: FormControl<boolean>;
  /** 明暗検知設定値 */
  lightExposureDetectValue: FormControl<string>;
  /** 衝撃検知 */
  shockDetected: FormControl<boolean>;
  /** 衝撃検知設定値 */
  shockDetectedDetectValue: FormControl<number>;
  /** 温度センサー */
  temperature: FormControl<boolean>;
  /** 温度センサー設定 */
  temperatureSettings: FormGroup<{
    /** 温度センサー：上限逸脱 */
    temperatureAlertMax: FormControl<number>;
    /** 温度センサー：上限警告 */
    temperaturePrealertMax: FormControl<number>;
    /** 温度センサー：下限警告 */
    temperaturePrealertMin: FormControl<number>;
    /** 温度センサー：下限逸脱 */
    temperatureAlertMin: FormControl<number>;
  }>;
  /** 気圧センサー */
  pressure: FormControl<boolean>;
  /** 気圧センサー設定 */
  pressureSettings: FormGroup<{
    /** 気圧センサー：上限逸脱 */
    pressureAlertMax: FormControl<number>;
    /** 気圧センサー：上限警告 */
    pressurePrealertMax: FormControl<number>;
    /** 気圧センサー：下限警告 */
    pressurePrealertMin: FormControl<number>;
    /** 気圧センサー：下限逸脱 */
    pressureAlertMin: FormControl<number>;
  }>;
  /** 湿度センサー */
  humidity: FormControl<boolean>;
  /** 湿度センサー設定 */
  humiditySettings: FormGroup<{
    /** 湿度センサー：上限逸脱 */
    humidityAlertMax: FormControl<number>;
    /** 湿度センサー：上限警告 */
    humidityPrealertMax: FormControl<number>;
    /** 湿度センサー：下限警告 */
    humidityPrealertMin: FormControl<number>;
    /** 湿度センサー：下限逸脱 */
    humidityAlertMin: FormControl<number>;
  }>;
  /** 傾斜センサー */
  tilt: FormControl<boolean>;
  /** 傾斜センサー設定 */
  tiltSettings: FormGroup<{
    /** 傾斜センサー：傾斜許容角度 */
    tiltAlertMax: FormControl<number>;
  }>;
  /** 照度センサー */
  light: FormControl<boolean>;
  /** 照度センサー設定 */
  lightSettings: FormGroup<{
    /** 照度センサー：上限逸脱 */
    lightAlertMax: FormControl<number>;
    /** 照度センサー：上限警告 */
    lightPrealertMax: FormControl<number>;
    /** 照度センサー：下限警告 */
    lightPrealertMin: FormControl<number>;
    /** 照度センサー：下限逸脱 */
    lightAlertMin: FormControl<number>;
  }>;
  /** フライトモード */
  fmd: FormControl<boolean>;
  /** 配送機能での利用許可 */
  shipmentTracking: FormControl<boolean>;
}>;

@Component({
  selector: 'app-asset-type-edit-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    ReactiveFormsModule,
    MatFormFieldModule,
    CommonModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatRadioModule,
    MatIcon,
    MatTooltipModule,
    MatIconModule,
    MatButtonModule,
    MinutesLabelPipe,
  ],
  templateUrl: './asset-type-edit-dialog.component.html',
  styleUrl: './asset-type-edit-dialog.component.scss',
})
export class AssetTypeEditDialogComponent
  implements AfterViewInit, AfterViewChecked, OnInit, OnDestroy
{
  /** ボタンの無効化状態 */
  public isDisabled: boolean = false;

  /** 設定テンプレート名 */
  public assetTypeName?: string;

  /** 更新中 */
  public updating: boolean;

  /** テナントID */
  public tenantId: string;

  /** ユーザーID */
  public userId: string;

  /** 設定テンプレート一覧 */
  public assetType?: AssetTypeViewModel;

  /** デバイス種類リスト */
  public deviceTypeList: DeviceTypeViewModel[];

  /** デバイス種類 */
  public deviceType?: DeviceTypeViewModel;

  /** フォームグループ */
  public formGroup: FormGroup;

  /** QTS110用デバイス種類 */
  public deviceTypeQTS110?: DeviceTypeValidatorViewModel;

  /** 測定間隔リスト */
  public samplingFrequencies: number[] = this.createFormGroup.defaultIntervals;

  /** 送信間隔リスト */
  public reportingFrequencies: number[] = this.createFormGroup.defaultIntervals;

  /** 位置測位技術優先度のリスト */
  public locationPriorities: { [key: string]: string } =
    this.createFormGroup.locationPriorities;

  /** 位置測位技術優先度のドロップダウン表示数 */
  public locationPrioritiesNum: number;

  /** 明暗検知設定値のリスト */
  public lightExposureDetectValues: { [key: string]: string } =
    this.createFormGroup.defaultLightExposureDetectValues;

  /** 衝撃検知設定値のリスト */
  public shockDetectedDetectValues: number[] =
    this.createFormGroup.defaultShockDetectedDetectValues;

  /** デバイス設定詳細初期表示値 */
  public initAssetType: DeviceSettingBase;

  /** トグルOFF時の変更先の設定値 */
  public assettypeRestoreValue: DeviceSettingBase;

  /** ツールチップメッセージ */
  public tooltipMessage: string;

  /** コンポーネント破棄時にObservableを解放するためのSubject */
  private destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) options: DialogOption,
    private dialogRef: MatDialogRef<AssetTypeFormGroup>,
    private dialog: MatDialog,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private createFormGroup: CreateQTSFormGroup,
  ) {
    this.isDisabled = options.isDisabled;
    this.updating = options.updating;
    this.tenantId = options.tenantId;
    this.userId = options.userId;
    this.assetType = options.target;
    this.deviceTypeList = options.deviceTypes.slice().sort((a, b) =>
      compareDisplayNames(a.displayName, b.displayName),
    );
    if (options.target?.deviceTypeKey) {
      this.deviceType = options.deviceTypes.find(
        (device) => device.deviceTypeKey === this.assetType?.deviceTypeKey,
      );
      this.deviceTypeQTS110 = options.deviceTypes.find(
        (device) => device.deviceTypeKey === this.assetType?.deviceTypeKey,
      );
    }
    this.locationPriorities = this.createFormGroup.updateLocationPriorities(
      this.deviceType?.locationTechCellular,
      this.deviceType?.locationTechGnss,
      this.deviceType?.locationTechWifi,
    );
    if (this.deviceType?.samplingFrequency) {
      this.samplingFrequencies = this.deviceType?.samplingFrequency;
    }
    if (this.deviceType?.reportingFrequency) {
      this.reportingFrequencies = this.deviceType.reportingFrequency;
    }
    this.locationPrioritiesNum = Object.keys(this.locationPriorities).length;

    this.initAssetType = {
      shipmentTracking: options.target?.shipmentTracking || false,
      locationDetected: options.target?.locationDetected || false,
      locationPriority: options.target?.locationPriority,
      fallDetected: options.target?.fallDetected || false,
      mcdDetected: options.target?.mcdDetected || false,
      lightExposureDetected: options.target?.lightExposureDetected || false,
      lightExposureDetectValue: options.target?.lightExposureDetectValue || '',
      shockDetected: options.target?.shockDetected || false,
      shockDetectedDetectValue: options.target?.shockDetectedDetectValue || 0,
      temperature: options.target?.temperature || false,
      temperatureSettings: options.target?.temperatureSettings,
      pressure: options.target?.pressure || false,
      pressureSettings: options.target?.pressureSettings,
      humidity: options.target?.humidity || false,
      humiditySettings: options.target?.humiditySettings,
      tilt: options.target?.tilt || false,
      tiltSettings: options.target?.tiltSettings,
      light: options.target?.light || false,
      lightSettings: options.target?.lightSettings,
    };

    this.assettypeRestoreValue = this.initAssetType;

    this.formGroup = this.createFormGroup.createQTSFormGroup(
      options.target,
      this.deviceTypeQTS110,
    );

    // [#159]端末側の不具合のため、動作は設定不可とする
    this.formGroup.controls['mcdDetected'].setValue(false, {
      emitEvent: false,
    });
    this.formGroup.controls['mcdDetected'].disable();

    this.formGroup.addControl(
      'assetTypeName',
      new FormControl<string>(options.target?.assettypeName || '', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(128)],
      }),
    );

    this.formGroup.addControl(
      'deviceTypeId',
      new FormControl<string>(options.target?.deviceTypeKey || '', {
        nonNullable: true,
        validators: Validators.required,
      }),
    );
    if (this.isDisabled) {
      this.formGroup.disable();
    }

    this.formGroup.addControl(
      'shipmentTracking',
      new FormControl<boolean>(options.target?.shipmentTracking || false, {
        nonNullable: true,
      }),
    );

    this.tooltipMessage =
      'ON：配送設定での利用を許可する　\n' +
      'OFF：配送設定での利用を許可しない';
  }

  ngOnInit(): void {
    // 各センサーのトグル監視をまとめて登録
    sensorsHavingForm.forEach((sensor) => this.watchSensorToggle(sensor));
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewChecked(): void {
    this.changeDetectorRef.detectChanges();
  }

  ngAfterViewInit(): void {
    this.createFormGroup.subscribeFormChanges(this.formGroup);
  }

  /**
   * 送信
   */
  public async submit() {
    const assetTypeName =
      this.formGroup.get('assetTypeName')?.value || this.assetTypeName;
    const operation = this.updating ? '変更' : '新規登録';
    const options: AlertDialogOption = {
      type: 'warn',
      title: 'Confirm',
      message: `設定テンプレート「${assetTypeName}」を${operation}してよろしいですか？`,
      operation: operation,
    };

    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      return;
    }
    // Qualcomm側でエラーとならないように、disabledの項目もリクエストに設定する。(disabledの場合はデバイス種類のデフォルト値が設定されている。)
    const formGroupWithDisableData = this.formGroup.getRawValue();

    const assetType: AssetTypeResponseModel = {
      assetTypeId: this.assetType?.assettypeId || '',
      assetTypeName: formGroupWithDisableData.assetTypeName || '',
      deviceActiveStatus: this.assetType?.deviceActiveStatus || false,
      deviceTypeId: this.deviceType?.deviceTypeId || '',
      deviceTypeKey: this.deviceType?.deviceTypeKey || '',
      samplingFrequency: formGroupWithDisableData.samplingFrequency || null,
      reportingFrequency: formGroupWithDisableData.reportingFrequency || null,
      shipmentTracking: formGroupWithDisableData.shipmentTracking || false,
      createdBy: this.updating ? this.assetType!.createdBy : this.userId,
      updatedBy: this.updating ? this.assetType!.updatedBy : this.userId,
      createdAt: this.updating
        ? this.assetType!.createdAt
        : formatISO(new Date()),
      updatedAt: this.updating
        ? this.assetType!.updatedAt
        : formatISO(new Date()),
    };

    //有効化されているものだけ値を追加する。
    if (this.deviceType?.locationSensor) {
      assetType.locationDetected =
        formGroupWithDisableData.locationDetected || false;
      assetType.locationPriority_01 =
        formGroupWithDisableData.locationPriority?.locationPriority1 || '';
      assetType.locationPriority_02 =
        formGroupWithDisableData.locationPriority?.locationPriority2 || '';
      assetType.locationPriority_03 =
        formGroupWithDisableData.locationPriority?.locationPriority3 || '';
    }
    if (this.deviceType?.fallDetected) {
      assetType.fallDetected = formGroupWithDisableData.fallDetected || false;
    }
    if (this.deviceType?.mcdDetected) {
      assetType.mcdDetected = formGroupWithDisableData.mcdDetected || false;
    }
    if (this.deviceType?.lightExposureDetected) {
      assetType.lightExposureDetected =
        formGroupWithDisableData.lightExposureDetected || false;
      assetType.lightExposureDetectValue =
        formGroupWithDisableData.lightExposureDetectValue || 'continuous';
    }
    if (this.deviceType?.shockDetected) {
      assetType.shockDetected = formGroupWithDisableData.shockDetected || false;
      assetType.shockDetectedDetectValue =
        formGroupWithDisableData.shockDetectedDetectValue || 0;
    }
    if (this.deviceType?.temperatureSensor) {
      assetType.temperature = formGroupWithDisableData.temperature || false;
      assetType.temperatureAlertMax =
        formGroupWithDisableData.temperatureSettings?.temperatureAlertMax || 0;
      assetType.temperatureAlertMin =
        formGroupWithDisableData.temperatureSettings?.temperatureAlertMin || 0;
      assetType.temperaturePrealertMax =
        formGroupWithDisableData.temperatureSettings?.temperaturePrealertMax ||
        0;
      assetType.temperaturePrealertMin =
        formGroupWithDisableData.temperatureSettings?.temperaturePrealertMin ||
        0;
    }
    if (this.deviceType?.pressureSensor) {
      assetType.pressure = formGroupWithDisableData.pressure || false;
      assetType.pressureAlertMax =
        formGroupWithDisableData.pressureSettings?.pressureAlertMax || 0;
      assetType.pressureAlertMin =
        formGroupWithDisableData.pressureSettings?.pressureAlertMin || 0;
      assetType.pressurePrealertMax =
        formGroupWithDisableData.pressureSettings?.pressurePrealertMax || 0;
      assetType.pressurePrealertMin =
        formGroupWithDisableData.pressureSettings?.pressurePrealertMin || 0;
    }
    if (this.deviceType?.humiditySensor) {
      assetType.humidity = formGroupWithDisableData.humidity || false;
      assetType.humidityAlertMax =
        formGroupWithDisableData.humiditySettings?.humidityAlertMax || 0;
      assetType.humidityAlertMin =
        formGroupWithDisableData.humiditySettings?.humidityAlertMin || 0;
      assetType.humidityPrealertMax =
        formGroupWithDisableData.humiditySettings?.humidityPrealertMax || 0;
      assetType.humidityPrealertMin =
        formGroupWithDisableData.humiditySettings?.humidityPrealertMin || 0;
    }
    if (this.deviceType?.tiltSensor) {
      assetType.tilt = formGroupWithDisableData.tilt || false;
      assetType.tiltAlertMax =
        formGroupWithDisableData.tiltSettings?.tiltAlertMax || 0;
    }
    if (this.deviceType?.lightSensor) {
      assetType.light = formGroupWithDisableData.light || false;
      assetType.lightAlertMax =
        formGroupWithDisableData.lightSettings?.lightAlertMax || 0;
      assetType.lightAlertMin =
        formGroupWithDisableData.lightSettings?.lightAlertMin || 0;
      assetType.lightPrealertMax =
        formGroupWithDisableData.lightSettings?.lightPrealertMax || 0;
      assetType.lightPrealertMin =
        formGroupWithDisableData.lightSettings?.lightPrealertMin || 0;
    }
    if (this.deviceType?.fmd) {
      assetType.fmd = formGroupWithDisableData.fmd || false;
    }

    this.dialogRef.close(assetType);
  }

  /**
   * デバイス種類を選択後、フォーム状態を再生成
   * @param deviceTypeId デバイス種類ID
   */
  public selectDeviceType(deviceTypeKey: string) {
    const targetDeviceTypeId = this.deviceTypeList.filter(
      (deviceType) => deviceType.deviceTypeKey === deviceTypeKey,
    )[0];
    this.deviceType = targetDeviceTypeId;
    this.createForm(targetDeviceTypeId);

    this.createFormGroup.subscribeFormChanges(this.formGroup);

    // 各センサーのトグル監視をまとめて登録（フォームが作り変えられるため再度実行）
    sensorsHavingForm.forEach((sensor) => this.watchSensorToggle(sensor));
  }

  /**
   * 送信間隔の選択肢が選択可能（非活性）か
   *
   * @param reportingFrequency 送信間隔の選択肢
   * @returns trueなら選択可能
   */
  public getDisabledReportingFrequency(reportingFrequency: number) {
    const samplingFrequency =
      this.formGroup.controls['samplingFrequency'].value;

    return this.createFormGroup.isDisabledReportingFrequency(
      samplingFrequency,
      reportingFrequency,
    );
  }

  public getLocationPriorityDisabled(
    value: string,
    compareValue1: string | null,
    compareValue2: string | null,
  ) {
    return this.createFormGroup.isLocationPriorityDisabled(
      value,
      compareValue1,
      compareValue2,
    );
  }

  /**
   * デバイス種類変更時、各フォームの値を設定
   * @param target デバイス種類情報
   */
  private createForm(target: DeviceTypeViewModel) {
    this.deviceTypeQTS110 = target;
    this.samplingFrequencies =
      target.samplingFrequency?.filter(
        (item) => item !== null && item !== undefined,
      ) || this.samplingFrequencies;
    this.reportingFrequencies =
      target.reportingFrequency || this.reportingFrequencies;

    this.locationPriorities = this.createFormGroup.updateLocationPriorities(
      target.locationTechCellular,
      target.locationTechGnss,
      target.locationTechWifi,
    );
    this.locationPrioritiesNum = Object.keys(this.locationPriorities).length;

    const assetTypeName = this.formGroup.get('assetTypeName')?.value;

    let targetData: DeviceModel;

    if (target.defaultTracker) {
      const priorities = [
        target.defaultTracker.locationPriority_01 ?? null,
        target.defaultTracker.locationPriority_02 ?? null,
        target.defaultTracker.locationPriority_03 ?? null,
      ];

      const temperatureSettings = [
        target.defaultTracker.temperatureAlertMax ?? null,
        target.defaultTracker.temperaturePrealertMax ?? null,
        target.defaultTracker.temperaturePrealertMin ?? null,
        target.defaultTracker.temperatureAlertMin ?? null,
      ];

      const pressureSettings = [
        target.defaultTracker.pressureAlertMax ?? null,
        target.defaultTracker.pressurePrealertMax ?? null,
        target.defaultTracker.pressurePrealertMin ?? null,
        target.defaultTracker.pressureAlertMin ?? null,
      ];

      const humiditySettings = [
        target.defaultTracker.humidityAlertMax ?? null,
        target.defaultTracker.humidityPrealertMax ?? null,
        target.defaultTracker.humidityPrealertMin ?? null,
        target.defaultTracker.humidityAlertMin ?? null,
      ];

      const tiltSettings = [
        target.defaultTracker.tiltAlertMax ?? null,
        target.defaultTracker.tiltPrealertMax ?? null,
        target.defaultTracker.tiltPrealertMin ?? null,
        target.defaultTracker.tiltAlertMin ?? null,
      ];

      const lightSettings = [
        target.defaultTracker.lightAlertMax ?? null,
        target.defaultTracker.lightPrealertMax ?? null,
        target.defaultTracker.lightPrealertMin ?? null,
        target.defaultTracker.lightAlertMin ?? null,
      ];

      targetData = {
        samplingFrequency: target.defaultTracker.samplingFrequency ?? null,
        reportingFrequency: target.defaultTracker.reportingFrequency ?? null,
        shipmentTracking: target.defaultTracker.shipmentTracking ?? false,
        locationDetected:
          target.defaultTracker.locationDetected ?? target.locationSensor,
        locationPriority: priorities,
        fallDetected: target.defaultTracker.fallDetected,
        mcdDetected: target.defaultTracker.mcdDetected,
        lightExposureDetected: target.defaultTracker.lightExposureDetected,
        lightExposureDetectValue:
          target.defaultTracker.lightExposureDetectValue,
        shockDetected: target.defaultTracker.shockDetected,
        shockDetectedDetectValue:
          target.defaultTracker.shockDetectedDetectValue,
        temperature: target.defaultTracker.temperature,
        temperatureSettings: temperatureSettings,
        pressure: target.defaultTracker.pressure,
        pressureSettings: pressureSettings,
        humidity: target.defaultTracker.humidity,
        humiditySettings: humiditySettings,
        tilt: target.defaultTracker.tilt,
        tiltSettings: tiltSettings,
        light: target.defaultTracker.light,
        lightSettings: lightSettings,
        fmd: target.defaultTracker.fmd,
      };
    } else {
      targetData = {
        samplingFrequency: null,
        reportingFrequency: null,
        shipmentTracking: false,
        locationDetected: target.locationSensor,
        locationPriority: Object.values(
          this.formGroup.get('locationPriority')?.value,
        ),
        fallDetected: target.fallDetected,
        mcdDetected: target.mcdDetected,
        lightExposureDetected: target.lightExposureDetected,
        lightExposureDetectValue: target.lightExposureDetectedValue,
        shockDetected: target.shockDetected,
        shockDetectedDetectValue: target.shockDetectedDetectValue,
        temperature: target.temperatureSensor,
        pressure: target.pressureSensor,
        humidity: target.humiditySensor,
        tilt: target.tiltSensor,
        light: target.lightSensor,
        fmd: target.fmd,
      };
    }

    this.formGroup = this.createFormGroup.createQTSFormGroup(
      targetData,
      this.deviceTypeQTS110,
    );

    this.formGroup.addControl(
      'assetTypeName',
      new FormControl<string>(assetTypeName || '', {
        nonNullable: true,
        validators: [Validators.required],
      }),
    );

    this.formGroup.addControl(
      'deviceTypeId',
      new FormControl<string>(target?.deviceTypeKey || '', {
        nonNullable: true,
      }),
    );

    this.formGroup.addControl(
      'shipmentTracking',
      new FormControl<boolean>(targetData?.shipmentTracking || false, {
        nonNullable: true,
      }),
    );

    // [#159]端末側の不具合のため、動作は設定不可とする
    this.formGroup.controls['mcdDetected'].setValue(false, {
      emitEvent: false,
    });
    this.formGroup.controls['mcdDetected'].disable();

    // デバイス種類毎の設定値をトグルをOFFにしたときの戻り値として設定。一部型に沿うように変換。
    this.assettypeRestoreValue = {
      shipmentTracking: this.initAssetType.shipmentTracking || false,
      locationDetected: targetData.locationDetected || false,
      locationPriority: targetData.locationPriority?.filter(
        (value): value is string => value != null,
      ),
      fallDetected: targetData.fallDetected || false,
      mcdDetected: targetData.mcdDetected || false,
      lightExposureDetected: targetData.lightExposureDetected || false,
      lightExposureDetectValue: targetData.lightExposureDetectValue || '',
      shockDetected: targetData.shockDetected || false,
      shockDetectedDetectValue: targetData.shockDetectedDetectValue || 0,
      temperature: targetData.temperature || false,
      temperatureSettings: targetData.temperatureSettings?.filter(
        (value): value is number => value != null,
      ),
      pressure: targetData.pressure || false,
      pressureSettings: targetData.pressureSettings?.filter(
        (value): value is number => value != null,
      ),
      humidity: targetData.humidity || false,
      humiditySettings: targetData.humiditySettings?.filter(
        (value): value is number => value != null,
      ),
      tilt: targetData.tilt || false,
      tiltSettings: targetData.tiltSettings?.filter(
        (value): value is number => value != null,
      ),
      light: targetData.light || false,
      lightSettings: targetData.lightSettings?.filter(
        (value): value is number => value != null,
      ),
    };
  }

  /**
   * 送信間隔の値が送信間隔のリストに含まれているか判定する。
   */
  public reportingFrequencyValueChanged(): void {
    if (
      !this.reportingFrequencies.includes(
        this.formGroup.get('reportingFrequency')?.value,
      )
    ) {
      this.formGroup.controls['reportingFrequency'].setValue(null);
    }
  }

  /**
   * トグルの変更を監視して、トグルがOFFになったときに入力フォームを初期値に戻す
   */
  private watchSensorToggle(sensor: string): void {
    const control = this.formGroup.get(sensor);
    if (!control) {
      return;
    }

    control.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((val: boolean) => {
        // OFF (false) になったときにリセット
        if (val === false) {
          resetSensorSettings(
            this.formGroup,
            this.assettypeRestoreValue,
            sensor,
          );
        }
      });
  }
}
